import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Building2, ChevronRight } from "lucide-react";
import ViewToggle, { useViewMode } from "@/components/admin/ViewToggle";
import { useLang } from "@/lib/lang";

export default function Fleets() {
  const { t } = useLang();
  const nav = useNavigate();
  const [fleets, setFleets] = useState([]);
  const [show, setShow] = useState(false);
  const [mode, setMode] = useViewMode("fleets");
  const load = () => api.get("/admin/fleets").then((r) => setFleets(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (f, status, e) => {
    e.stopPropagation();
    try { await api.patch(`/admin/fleets/${f.id}`, { status }); toast.success(`Fleet ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const open = (f) => nav(`/admin/fleets/${f.id}`);
  return (
    <div className="p-10 max-w-[1600px]">
      <PageHeader eyebrow={t("nav_fleets")} title={t("nav_fleets")} description="—">
        <ViewToggle scope="fleets" value={mode} onChange={setMode} testId="fleets-view-toggle" />
        <GoldButton onClick={() => setShow(true)} data-testid="new-fleet-btn"><Plus className="w-4 h-4" /> {t("fleets_new")}</GoldButton>
      </PageHeader>
      {fleets.length === 0 && <div className="border border-dashed border-slate-200 p-16 text-center text-slate-500">—</div>}
      {mode === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {fleets.map((f) => (
            <div key={f.id} onClick={() => open(f)} className="border border-slate-200 bg-white p-6 cursor-pointer hover:border-[#f59e0b] transition-colors" data-testid={`fleet-${f.id}`}>
              <div className="flex items-start justify-between mb-4">
                <Building2 className="w-6 h-6 text-[#f59e0b]" />
                <StatBadge status={f.status} />
              </div>
              <h3 className="font-display text-lg text-[#0a0f1e]">{f.company_name}</h3>
              <div className="text-xs text-slate-600 font-semibold">NIF {f.nif}</div>
              <div className="mt-4 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Rep. legal</span><span className="text-[#0a0f1e] font-semibold">{f.legal_representative || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">{t("fleets_earnings")}</span><span className="text-[#0a0f1e] font-mono font-bold">€{(f.total_earnings || 0).toFixed(2)}</span></div>
              </div>
              <div className="mt-5 flex gap-2 items-center">
                {f.status !== "approved" && <GoldButton onClick={(e) => setStatus(f, "approved", e)} className="!h-8 !px-3 !text-[10px]">{t("approve")}</GoldButton>}
                {f.status !== "suspended" && <GhostButton onClick={(e) => setStatus(f, "suspended", e)} className="!h-8 !px-3 !text-[10px]">{t("status_suspended")}</GhostButton>}
                <Link to={`/admin/fleets/${f.id}`} onClick={(e) => e.stopPropagation()} data-testid={`open-fleet-${f.id}`} className="ml-auto text-[10px] uppercase tracking-widest text-[#f59e0b] hover:underline font-bold inline-flex items-center gap-1">
                  Abrir <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="fleets-list-view">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">{t("company")}</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">NIF</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">Rep. legal</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">{t("email")}</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700 text-right">{t("fleets_earnings")}</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700">{t("status")}</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-slate-700 text-right">{t("actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {fleets.map((f) => (
                <tr key={f.id} onClick={() => open(f)} data-testid={`fleet-row-${f.id}`} className="hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-3 font-semibold text-[#0a0f1e]">{f.company_name}</td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{f.nif}</td>
                  <td className="px-4 py-3 text-slate-700">{f.legal_representative || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{f.email || "—"}</td>
                  <td className="px-4 py-3 font-mono text-right text-[#0a0f1e] font-bold">€{(f.total_earnings || 0).toFixed(2)}</td>
                  <td className="px-4 py-3"><StatBadge status={f.status} /></td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {f.status !== "approved" && <button onClick={(e) => setStatus(f, "approved", e)} className="text-[10px] uppercase tracking-widest text-emerald-700 hover:text-emerald-900 font-bold">{t("approve")}</button>}
                    {f.status !== "suspended" && <button onClick={(e) => setStatus(f, "suspended", e)} className="text-[10px] uppercase tracking-widest text-slate-700 hover:text-red-700 font-bold">{t("status_suspended")}</button>}
                    <Link to={`/admin/fleets/${f.id}`} onClick={(e) => e.stopPropagation()} data-testid={`open-fleet-row-${f.id}`} className="text-[10px] uppercase tracking-widest text-[#f59e0b] hover:underline font-bold">{t("action_open")}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {show && <CreateFleet onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
    </div>
  );
}

function CreateFleet({ onClose, onCreated }) {
  const { t } = useLang();
  const [f, setF] = useState({ company_name: "", nif: "", address: "", legal_representative: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/admin/fleets", f); toast.success(t("fleets_added_toast")); onCreated(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-6">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-md p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl text-[#0a0f1e]">{t("fleets_new")}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="space-y-4">
          {["company_name", "nif", "legal_representative", "address", "email", "phone"].map((k) => (
            <div key={k}>
              <label className="label-caps block mb-2">{k.replace(/_/g, " ")}</label>
              <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input-dt" required={["company_name", "nif"].includes(k)} />
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-6">
          <GhostButton type="button" onClick={onClose} className="flex-1">{t("action_cancel")}</GhostButton>
          <GoldButton type="submit" disabled={loading} className="flex-1 justify-center">{t("action_create")}</GoldButton>
        </div>
      </form>
    </div>
  );
}
